import { Box, Button, Icon } from '@chakra-ui/react'
import React, { useRef, useCallback, useMemo, useState, useEffect } from 'react'
import { VirtuosoGrid, VirtuosoGridHandle } from 'react-virtuoso'
import Selecto from 'react-selecto'
import makeGridComponents from './makeGridComponents'
import useColCountObserver from '../../../../hooks/useColCountObserver'
import Heading from './Heading'
import {
  MediaLibraryActions,
  MediaLibraryActionType,
} from '../../../../providers/MLProvider'
import { MediaLibrarySearchCollectionResponse } from '../../../../interfaces'
import { FiChevronDown } from 'react-icons/fi'
import { mediaCollectionsPerPage } from '../../../../../utils/mediaLibrary'
import NewCollectionElement from './NewCollectionElement'
import DragComponent from './Selecto'
import useGridKeyboardNavigation from '../../../../hooks/useKeyboardSelectionv2'

interface GridViewProps {
  scrollEl: HTMLDivElement | null
  dispatch: React.Dispatch<MediaLibraryActionType | MediaLibraryActionType[]>
  mediaCollectionList: MediaLibrarySearchCollectionResponse[]
  recentMediaCollectionList: MediaLibrarySearchCollectionResponse[]
  selectedMediaCollections: MediaLibrarySearchCollectionResponse[]
  loadingMediaCollections: boolean
  mediaCollectionPage: number
  minHeight: string
}

const CollectionGridNew = React.memo(
  ({
    scrollEl,
    dispatch,
    mediaCollectionList,
    recentMediaCollectionList,
    selectedMediaCollections,
    loadingMediaCollections,
    mediaCollectionPage,
    minHeight
  }: GridViewProps) => {
    const [colCount, setColCount] = useState(3)
    const [focusedIndex, setFocusedIndex] = useState<{
      index: number
      type: string
      id: string
    } | null>(null)
    const gridOrder = ['recent', 'mediaCollection']
    const assetCountMap = {
      recent: recentMediaCollectionList.length,
      mediaCollection: mediaCollectionList.length,
    }

    const recentRef = useRef<VirtuosoGridHandle>(null)
    const mediaCollectionRef = useRef<VirtuosoGridHandle>(null)

    const selectoRef = React.useRef<Selecto>(null)
    const keyActiveElementRef = React.useRef<HTMLDivElement | null>(null)

    const selectoDragMarker = React.useRef<{ x: number; y: number } | null>(
      null,
    )

    const assetRefMap = {
      recent: recentRef,
      mediaCollection: mediaCollectionRef,
    }

    const getIdMap = useMemo(
      () => ({
        recent: (index: number) => {
          const asset = recentMediaCollectionList[index]
          return asset ? asset.mediaCollectionId : ''
        },
        mediaCollection: (index: number) =>
          mediaCollectionList[index]?.mediaCollectionId ?? '',
      }),
      [recentMediaCollectionList, mediaCollectionList],
    )

    // preparing this set so that finding in recent items is O(1)
    const recentMediaCollectionSet = useMemo(() => {
      return new Set(
        recentMediaCollectionList.map(
          mediaCollection => mediaCollection.mediaCollectionId,
        ),
      )
    }, [recentMediaCollectionList])

    // preparing this set so that finding in fileList is O(1)
    const mediaCollectionMap = useMemo(() => {
      return new Map(
        mediaCollectionList.map(mediaCollection => [
          mediaCollection.mediaCollectionId,
          mediaCollection,
        ]),
      )
    }, [mediaCollectionList])

    const selectedAssetMap: Record<
      'recent' | 'mediaCollection',
      Set<string>
    > = useMemo(() => {
      const selectedRecentLocal = new Set<string>()
      const selectedMediaCollectionsLocal = new Set<string>()

      for (const f of selectedMediaCollections) {
        if (recentMediaCollectionSet.has(f.mediaCollectionId)) {
          selectedRecentLocal.add(f.mediaCollectionId)
        } else {
          selectedMediaCollectionsLocal.add(f.mediaCollectionId)
        }
      }

      return {
        recent: selectedRecentLocal,
        mediaCollection: selectedMediaCollectionsLocal,
      }
    }, [selectedMediaCollections, recentMediaCollectionSet])

    const setSelectedAssetMap = useMemo(
      () => ({
        recent: (newSet: Set<string>) => {
          const recentSelectedMediaCollections: MediaLibrarySearchCollectionResponse[] =
            recentMediaCollectionList.filter(i =>
              newSet.has(i.mediaCollectionId),
            )

          const existingSelectedMediaCollections: MediaLibrarySearchCollectionResponse[] =
            []
          selectedAssetMap['mediaCollection'].forEach(id => {
            const mediaCollection = mediaCollectionMap.get(id)
            if (mediaCollection) {
              existingSelectedMediaCollections.push(mediaCollection)
            }
          })
          dispatch([
            {
              type: MediaLibraryActions.SET_SELECTED_MEDIA_COLLECTIONS,
              payload: [
                ...recentSelectedMediaCollections,
                ...existingSelectedMediaCollections,
              ],
            },
          ])
        },
        mediaCollection: (newSet: Set<string>) => {
          const mediaCollections = mediaCollectionList.filter(f =>
            newSet.has(f.mediaCollectionId),
          )
          const recentMediaCollections: MediaLibrarySearchCollectionResponse[] =
            recentMediaCollectionList.filter(i =>
              selectedAssetMap.recent.has(i.mediaCollectionId),
            )
          dispatch({
            type: MediaLibraryActions.SET_SELECTED_MEDIA_COLLECTIONS,
            payload: [...mediaCollections, ...recentMediaCollections],
          })
        },
      }),
      [
        dispatch,
        mediaCollectionList,
        mediaCollectionMap,
        recentMediaCollectionList,
        selectedAssetMap,
      ],
    )

    const handleKeyDown = useGridKeyboardNavigation({
      gridOrder,
      assetCountMap,
      getIdMap,
      colCount,
      selectedAssetMap,
      setSelectedAssetMap,
      focusedIndex,
      setFocusedIndex,
      assetRefMap,
      dispatch,
      route: '/media-collections',
    })

    const gridComponentsMap = useMemo(
      () => ({
        recent: makeGridComponents(colCount, 'recent-grid'),
        mediaCollection: makeGridComponents(colCount, 'mediaCollection-grid'), //. it is important to keep className as ${gridType}-grid
      }),
      [colCount],
    )

    useColCountObserver(scrollEl, setColCount)

    const handleLoadMoreMediaCollections = React.useCallback(e => {
      e.stopPropagation()
      dispatch({ type: MediaLibraryActions.LOAD_MORE_MEDIA_COLLECTIONS })
    }, [])

    const refreshTargets = useCallback(
      () =>
        requestAnimationFrame(() =>
          selectoRef.current?.findSelectableTargets(),
        ),
      [selectoRef.current],
    )

    useEffect(() => {
      if (focusedIndex?.id) {
        const { index, type, id } = focusedIndex
        if (!selectedAssetMap[type].has(id)) {
          setFocusedIndex(null)
        }
      }
    }, [JSON.stringify(focusedIndex), selectedAssetMap])

    return (
      <Box
        height={'100%'}
        outline={'none'}
        width={'100%'}
        paddingX={'24px'}
        paddingTop={'24px'}
        ref={keyActiveElementRef}
        tabIndex={0}
        minHeight={minHeight}
        onClick={() => {
          // do not stop event propagation here, as we want to close any open context menu, stop it in MediaLibraryLayout
          // event.stopPropagation();

          // drag can happen in two ways
          //1.Complete drag inside the single grid element like inside files and folders, in that case click event is propogated to onCLick
          // listen of element and it does not comes to this outer box onCLick.
          //2. Drag end outside the grid element , or it covers multiple grid elements and in that case
          //  click event is propogated to this outer box onClick. So here we check if drag has occured we return it  else clear selection

          // if (
          //   selectoDragMarker.current &&
          //   (Math.abs(selectoDragMarker.current.x) > 5 ||
          //     Math.abs(selectoDragMarker.current.y) > 5)
          // ) {
          //   selectoDragMarker.current = null
          //   return
          // }
          setFocusedIndex(null)
          Object.entries(setSelectedAssetMap).forEach(([type, selected]) => {
            selectedAssetMap[type].clear()
            selected(selectedAssetMap[type])
          })
        }}
        onKeyDown={handleKeyDown}
      >
        {scrollEl && (
          <>
            <DragComponent
              selectedAssetMap={selectedAssetMap}
              setSelectedAssetMap={setSelectedAssetMap}
              setFocusedIndex={setFocusedIndex}
              selectoRef={selectoRef}
              scrollEl={scrollEl}
              selectoDragMarker={selectoDragMarker}
              assetCountMap={assetCountMap}
              getIdMap={getIdMap}
              colCount={colCount}
              gridOrder={gridOrder}
              keyActiveElementRef={keyActiveElementRef}
            ></DragComponent>

            {recentMediaCollectionList.length > 0 ? (
              <>
                <Heading
                  name="Recently created by you"
                  isFirst={true}
                  className="recent-heading"
                />
                <VirtuosoGrid
                  data={recentMediaCollectionList}
                  ref={recentRef}
                  customScrollParent={scrollEl}
                  components={gridComponentsMap.recent}
                  rangeChanged={refreshTargets}
                  overscan={0}
                  itemContent={(index, item) => (
                    <NewCollectionElement
                      gridTypeForDataset="recent"
                      getIdMap={getIdMap}
                      setFocusedIndex={setFocusedIndex}
                      selectedAssetMap={selectedAssetMap}
                      setSelectedAssetMap={setSelectedAssetMap}
                      focusedIndex={focusedIndex}
                      index={index}
                      dispatch={dispatch}
                      mediaCollection={
                        item as MediaLibrarySearchCollectionResponse
                      }
                      gridOrder={gridOrder}
                      assetCountMap={assetCountMap}
                    />
                  )}
                />
              </>
            ) : null}

            {mediaCollectionList.length > 0 && (
              <>
                <Heading
                  name="Media collections"
                  isFirst={recentMediaCollectionList.length === 0}
                  className="mediaCollection-heading"
                />
                <VirtuosoGrid
                  data={mediaCollectionList}
                  ref={mediaCollectionRef}
                  customScrollParent={scrollEl}
                  components={gridComponentsMap.mediaCollection}
                  rangeChanged={refreshTargets}
                  overscan={0}
                  itemContent={(index, item) => (
                    <NewCollectionElement
                      gridTypeForDataset="mediaCollection"
                      getIdMap={getIdMap}
                      setFocusedIndex={setFocusedIndex}
                      selectedAssetMap={selectedAssetMap}
                      setSelectedAssetMap={setSelectedAssetMap}
                      focusedIndex={focusedIndex}
                      index={index}
                      dispatch={dispatch}
                      mediaCollection={
                        item as MediaLibrarySearchCollectionResponse
                      }
                      gridOrder={gridOrder}
                      assetCountMap={assetCountMap}
                    />
                  )}
                />
                {(mediaCollectionList.length % mediaCollectionsPerPage === 0 &&
                  mediaCollectionList.length / mediaCollectionsPerPage ===
                    mediaCollectionPage + 1) ||
                loadingMediaCollections ? (
                  <Button
                    as="a"
                    variant="link"
                    cursor="pointer"
                    color="appClearBlue"
                    paddingTop={'16px'}
                    onClick={handleLoadMoreMediaCollections}
                    isLoading={loadingMediaCollections}
                    loadingText={'Loading...'}
                    className="mediaCollection-see-more"
                  >
                    {'See More'}
                    <Icon as={FiChevronDown} />
                  </Button>
                ) : null}
              </>
            )}
          </>
        )}
        <Box className="padding-bottom" height={'24px'}></Box>
      </Box>
    )
  },
)

export default CollectionGridNew
